import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: 'accurate' | 'normal';
  } = {}
) {
  const { decimals = 0, sizeType = 'normal' } = opts;

  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const accurateSizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB'];
  if (bytes === 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === 'accurate'
      ? (accurateSizes[i] ?? 'Bytest')
      : (sizes[i] ?? 'Bytes')
  }`;
}

export function getOrganizationInitials(organizationName: string): string {
  if (!organizationName || typeof organizationName !== 'string') {
    return '';
  }

  // Split the name into words, filter out small words if needed
  const words = organizationName.split(/\s+/).filter((word) => word.length > 0);

  // Handle cases where there's only one word
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }

  // Get first letters of first two significant words
  const initials = words
    .slice(0, 2) // Take first two words
    .map((word) => word.charAt(0).toUpperCase())
    .join('');

  return initials;
}

export function getUserInitials(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  // Extract the part before @ symbol
  const username = email.split('@')[0];
  
  // Get first letter and make it uppercase
  return username.charAt(0).toUpperCase();
}

export function formatUserRole(role: string): string {
  if (!role) return '';
  
  // Convert snake_case to Title Case
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function isStringUpperCase(str: string) {
  return str === str.toUpperCase();
}

export function cleanDeep<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(cleanDeep).filter(v => v != null && v !== "") as T;
  }
  if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, cleanDeep(v)])
        .filter(([_, v]) => v != null && v !== "")
    ) as T;
  }
  return obj;
}

export function getMediaType(url: string): 'image' | 'pdf' | 'document' | 'link' | 'other' {
  if (!url) return 'other';
  
  const lowerUrl = url.toLowerCase();
  const extension = lowerUrl.split('.').pop()?.split('?')[0] || '';
  
  // Check for image extensions
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) {
    return 'image';
  }
  
  // Check for PDF
  if (extension === 'pdf' || lowerUrl.includes('.pdf')) {
    return 'pdf';
  }
  
  // Check for document extensions
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf'].includes(extension)) {
    return 'document';
  }
  
  // Check if it's a web link (starts with http/https and doesn't have a file extension)
  if ((url.startsWith('http://') || url.startsWith('https://')) && !extension.match(/^[a-z0-9]{1,5}$/)) {
    return 'link';
  }
  
  return 'other';
}